import React from 'react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import type { AdvanceInvoiceBalance } from '../api/invoiceWorkflowApi';
import { formatAmountGrouped } from '../../../utils/currency';
import { recognitionMonthLabel } from '../invoiceRecognitionMonth';

const signed = (value: number) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatAmountGrouped(Math.abs(value))} zł`;

/**
 * Advance / settlement invoices of one ZK and their balance against its sale
 * value (backend salesAdvanceInvoiceService.balance). Informational: the
 * warning never blocks the approval (Emil, 2026-10-19).
 */
const InvoiceAdvanceBalance: React.FC<{ balance: AdvanceInvoiceBalance }> = ({ balance }) => {
  const { t } = useTranslation('invoices');
  const documents = balance.documents.map(document => t('invoices:workflow.amountInvoice.document', {
    kind: t(`invoices:workflow.kind.${document.documentKind.toLowerCase()}`),
    number: document.number || '—',
    amount: `${document.netPLN < 0 ? '−' : ''}${formatAmountGrouped(Math.abs(document.netPLN))} zł`,
    month: document.recognitionMonth ? recognitionMonthLabel(document.recognitionMonth) : '—',
  }));
  const summary = t('invoices:workflow.amountInvoice.summary', {
    invoiced: `${formatAmountGrouped(balance.invoicedNetPLN)} zł`,
    order: balance.orderNetPLN == null ? '—' : `${formatAmountGrouped(balance.orderNetPLN)} zł`,
    balance: balance.balancePLN == null ? '—' : signed(balance.balancePLN),
  });
  return <Alert
    type={balance.warning ? 'warning' : 'info'}
    showIcon
    data-testid="invoice-advance-balance"
    message={balance.warning?.message ?? summary}
    description={<>
      {balance.warning && <div>{summary}</div>}
      <ul style={{ margin: 0, paddingInlineStart: 18 }}>{documents.map(line => <li key={line}>{line}</li>)}</ul>
    </>}
  />;
};

export default InvoiceAdvanceBalance;
